import { createContext, useEffect, useRef, useState, type ReactElement } from "react";

import { cardFailedMessage, cardUnavailableMessage, listen, prepareCheckout, releaseCheckout } from "./card-token.js";

/** カードの入力を開くボタンに渡すもの。 */
export interface CardCheckoutProps {
    readonly publicKey: string;
    /** ボタンの文言（「カード情報を入力して申し込む」など）。 */
    readonly text: string;
    /** PAY.JP の入力欄の送信ボタン（「申し込む」）。<b>無ければ PAY.JP の既定</b>（今のプラン変更と同じ）。 */
    readonly submitText?: string | undefined;
    /** 送っている間など、押させないとき。 */
    readonly disabled: boolean;
    /** 押されたとき（前の断りを消す）。 */
    readonly onOpen: () => void;
    /**
     * カードの入力と 3-D セキュアが済み、トークンが返ったとき。画面はそのまま確定の口へ送る
     * （PAY.JP の入力画面の送信が最後の確認なので、ブラウザの確認ダイアログは出さない。docs/01 §7）。
     */
    readonly onToken: (token: string) => void;
    /** 読み込めない・カードを断られた・3-D セキュアに失敗したとき（画面に出す文）。 */
    readonly onError: (message: string) => void;
}

/**
 * 文言と押せるかを、PAY.JP のボタンに写す。<b>トークンを受け取ると PAY.JP が「入力済み」の文言と色に変える</b>ので、
 * そのたびに戻す（確定の口に断られたら、もう一度押して入力し直す。受け取ったトークンは残さない）。
 */
const paint = (input: HTMLInputElement, props: CardCheckoutProps): void => {
    input.value = props.text;
    input.className = "";
    input.disabled = props.disabled;
    const hidden = input.parentElement?.querySelector<HTMLInputElement>("input[name=payjp-token]");
    if (hidden != null) {
        hidden.value = "";
    }
};

/**
 * 既定。<b>PAY.JP が作ったボタンそのもの</b>を、この部品の場所へ移して見せる（<c>card-token.ts</c>）。
 * 読み込みは画面を開いたときに始める。押せるようになるまでは、同じ文言の押せないボタンを出しておく。
 * 閉じられたら何も返らない（もう一度押せば入力し直せる）。
 */
export function PayjpCheckoutButton(props: CardCheckoutProps): ReactElement {
    const slot = useRef<HTMLDivElement>(null);
    const [button, setButton] = useState<HTMLInputElement | null>(null);
    // 受け口は描くたびに最新にする（PAY.JP は読み込んだときの関数名を呼ぶだけ）
    const latest = useRef(props);
    const { publicKey, submitText } = props;

    useEffect(() => {
        let active = true;
        let box: HTMLElement | null = null;
        const onClick = (): void => { latest.current.onOpen(); };
        void prepareCheckout(publicKey, submitText).then((prepared) => {
            if (!active) {
                return;
            }
            const input = prepared?.querySelector<HTMLInputElement>("input[type=button]") ?? null;
            if (prepared === null || input === null || slot.current === null) {
                latest.current.onError(cardUnavailableMessage);
                return;
            }
            box = prepared;
            listen({
                // PAY.JP が受け口を呼んだ後にボタンを書き換えるので、それが済んでから戻して渡す
                created: (token) => {
                    setTimeout(() => {
                        paint(input, latest.current);
                        latest.current.onToken(token);
                    }, 0);
                },
                failed: () => { latest.current.onError(cardFailedMessage); },
            });
            input.addEventListener("click", onClick);
            slot.current.append(prepared);
            setButton(input);
        });
        return () => {
            active = false;
            if (box !== null) {
                box.querySelector("input[type=button]")?.removeEventListener("click", onClick);
                releaseCheckout(box);
            }
        };
    }, [publicKey, submitText]);

    useEffect(() => {
        latest.current = props;
        if (button !== null) {
            paint(button, props);
        }
    });

    return (
        <div className="card-checkout grow" data-card-checkout="">
            {button === null
                ? <button type="button" className="btn-primary w-full" disabled>{props.text}</button>
                : null}
            <div ref={slot} />
        </div>
    );
}

/** カードの入力を開くボタンを描く。 */
export type RenderCardCheckout = (props: CardCheckoutProps) => ReactElement;

/** カードの入力を開くボタン。<b>差し替えられる形にしてある</b>——テストは PAY.JP へ出られない。 */
export const CardCheckoutContext = createContext<RenderCardCheckout>((props) => <PayjpCheckoutButton {...props} />);
